import type { DefaultValues } from 'react-hook-form';
import type {
  AllocationFormValues,
  EditAllocationTarget,
} from '../components/AllocationForm.schema';
import type { ProjectOption } from '../types/Project';
import { parseApiDate, toApiDate } from './data';
import { contractLabel, professionalLabel } from './labels';

export function projectBills(
  project: Pick<ProjectOption, 'isPool' | 'isInternal'> | undefined,
): boolean {
  return !project?.isPool && !project?.isInternal;
}

export function initialValues(
  target: EditAllocationTarget | null,
  today: string,
): DefaultValues<AllocationFormValues> {
  if (!target) {
    return {
      professional: undefined,
      contract: undefined,
      project: undefined,
      allocatedPercentage: 100,
      billable: true,
      startDate: parseApiDate(today) as Date,
      endDate: null,
      costShareBr: 100,
      costShareUs: 0,
      // `''` em vez de `undefined`: senão o form nasce `isDirty` e fechar pede confirmação.
      notes: '',
      fimConfirmado: false,
      tagIds: [],
      notified: false,
      noticeDate: null,
      noticeDays: 0,
      salesProfileId: null,
      monthlyCost: null,
      hourlyRate: 0,
      soldHours: 0,
      costCurrency: null,
      billingCurrency: null,
    };
  }
  const { allocation, professional } = target;
  const projectAsOption: ProjectOption = {
    id: allocation.project.id,
    axisCode: allocation.project.axisCode,
    name: allocation.project.name,
    contract: allocation.contract,
    client: allocation.client,
    unit: allocation.unit,
    isPool: allocation.project.isPool,
    isInternal: allocation.project.isInternal,
  };
  return {
    professional: {
      id: professional.id,
      name: professionalLabel(professional),
    },
    contract: { id: allocation.contract.id, name: contractLabel(allocation.contract) },
    project: projectAsOption,
    allocatedPercentage: allocation.allocatedPercentage,
    billable: allocation.billable,
    startDate: parseApiDate(allocation.startDate) as Date,
    endDate: parseApiDate(allocation.endDate),
    costShareBr: allocation.costShareBr,
    costShareUs: allocation.costShareUs,
    notes: allocation.notes ?? '',
    fimConfirmado: allocation.fimConfirmado,
    tagIds: allocation.tags.map((tag) => tag.id),
    notified: allocation.notified,
    noticeDate: parseApiDate(allocation.noticeDate),
    noticeDays: allocation.noticeDays,
    salesProfileId: allocation.salesProfile?.id ?? null,
    monthlyCost: allocation.monthlyCost,
    hourlyRate: allocation.hourlyRate ?? 0,
    soldHours: allocation.soldHours ?? 0,
    costCurrency: allocation.costCurrency,
    billingCurrency: allocation.billingCurrency,
  };
}

/** Fora de projeto de pool, o aviso vai vazio no corpo (o back também o limpa). */
export function noticeBody(
  values: Partial<Pick<AllocationFormValues, 'notified' | 'noticeDate' | 'noticeDays'>>,
  project: Pick<ProjectOption, 'isPool'> | undefined,
) {
  if (!project?.isPool || !values.notified) {
    return { notified: false, noticeDate: null, noticeDays: 0 };
  }
  return {
    notified: true,
    noticeDate: toApiDate(values.noticeDate ?? null),
    noticeDays: values.noticeDays ?? 0,
  };
}

/** Só os campos customizáveis vão no corpo; custo alocado e faturamento o back recalcula. */
export function valuesBody(
  values: Partial<
    Pick<AllocationFormValues, 'salesProfileId' | 'monthlyCost' | 'hourlyRate' | 'soldHours'>
  >,
) {
  return {
    salesProfileId: values.salesProfileId ?? null,
    monthlyCost: values.monthlyCost ?? undefined,
    hourlyRate: values.hourlyRate ?? 0,
    soldHours: values.soldHours ?? 0,
  };
}

export function formFieldFor(apiField: string): keyof AllocationFormValues | null {
  switch (apiField) {
    case 'professionalId':
      return 'professional';
    case 'projectId':
      return 'project';
    case 'allocatedPercentage':
    case 'billable':
    case 'startDate':
    case 'endDate':
    case 'costShareBr':
    case 'costShareUs':
    case 'notes':
    case 'fimConfirmado':
    case 'tagIds':
    case 'notified':
    case 'noticeDate':
    case 'noticeDays':
    case 'salesProfileId':
    case 'monthlyCost':
    case 'hourlyRate':
    case 'soldHours':
      return apiField;
    default:
      return null;
  }
}
